import { memo, useEffect } from 'react';

import { DropdownMenuSeparator } from '@air/primitive-dropdown-menu';

import { getStorageMeterColor } from '~/components/CurrentWorkspaceNav/utils';
import { StorageUpgradeCTA } from '~/components/Upsells/StorageUpgradeCTA';
import { TrialCTA } from '~/components/Upsells/TrialCTA';
import { WorkspaceStorageMeter } from '~/components/Workspace/WorkspaceStorageMeter';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useShouldShowStorageUsage } from '~/swr-hooks/subscriptions/useShouldShowStorageUsage';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { isSubscriptionOnTrial, shouldShowStorageUpgrade } from '~/utils/SubscriptionUtils';
import { getUsedStoragePercentage } from '~/utils/WorkspaceUtils';

export const WorkspaceHeaderInformationDropdownStorageItems = memo(() => {
  const { shouldShowStorageUsage } = useShouldShowStorageUsage();
  const { data: storage, refetch } = useStorage();
  const percentOfStorageUsed = getUsedStoragePercentage(storage?.usedStorage, storage?.maxStorage);
  const progressColor = getStorageMeterColor(percentOfStorageUsed || 0, storage?.isOutOfStorage || false);
  const { data: subscription } = useSubscriptionContext();
  const isOnTrial = isSubscriptionOnTrial(subscription);
  const showStorageUpgrade = shouldShowStorageUpgrade({ subscription, storage });

  /**
   * Whenever they open this popover, we want to refetch the storage data
   * to make sure we have the latest info but we don't want to do update the
   * stale time cause we don't want other places to fetch it
   *
   * @see https://air-labs-team.slack.com/archives/C018C7U0DQU/p1690572803551579
   */
  useEffect(() => {
    refetch();
  }, [refetch]);

  if (!shouldShowStorageUsage) return null;

  return (
    <>
      <div className="px-1">
        {isOnTrial ? <TrialCTA /> : showStorageUpgrade && <StorageUpgradeCTA />}
        <WorkspaceStorageMeter
          progressColor={progressColor}
          formattedMaxBytes={storage?.formattedMaxBytes}
          formattedUsedBytes={storage?.formattedUsedBytes}
          percentOfStorageUsed={percentOfStorageUsed}
          showPercentage
        />
      </div>
      <DropdownMenuSeparator />
    </>
  );
});

WorkspaceHeaderInformationDropdownStorageItems.displayName = 'WorkspaceHeaderInformationDropdownStorage';
